/**
 * Phase definitions for the external-agent guided training tour
 * (MRG-281).
 *
 * Mirrors the Figma flow node 82:619: a centered Welcome modal, then
 * five numbered tour stops (one on `/properties`, four on the offers
 * page), then two pop-overs on the submit-property page guiding the
 * agent into actually submitting their first listing. Completion is
 * driven by the agent's action on the submit page (submit / save draft
 * / exit), not by clicking a "Finish" button — see
 * [CompletionModal.tsx](./CompletionModal.tsx).
 *
 * Driver.js handles the spotlights for the seven popover phases. The
 * Welcome and Completion screens are React modals
 * (see [WelcomeModal.tsx](./WelcomeModal.tsx) and
 * [CompletionModal.tsx](./CompletionModal.tsx)).
 */

import type { DriveStep } from 'driver.js'

export type AgentTrainingPhaseId =
  | 'step-1-listings'
  | 'step-2-offers-intro'
  | 'step-3-offers-email'
  | 'step-4-offers-actions'
  | 'step-5-next-submit'
  | 'submit-intro'
  | 'submit-actions'

/** Phases that run on the offers page (steps 2-5). */
export const OFFERS_PAGE_PHASES: AgentTrainingPhaseId[] = [
  'step-2-offers-intro',
  'step-3-offers-email',
  'step-4-offers-actions',
  'step-5-next-submit',
]

export const PHASE_ORDER: AgentTrainingPhaseId[] = [
  'step-1-listings',
  ...OFFERS_PAGE_PHASES,
  'submit-intro',
  'submit-actions',
]

/**
 * Driver.js step descriptors per phase. Copy is taken verbatim from the
 * Figma tooltip frames.
 *
 * Note: titles render as the "Step N of 5" indicator (Sea green); the
 * description carries the body copy. Submit-page popovers have no step
 * title (Figma node 62:486 / 62:557).
 */
export const PHASE_STEPS: Record<AgentTrainingPhaseId, DriveStep> = {
  // Steps 1, 2, 3, 5 hang below their respective navbar tabs (Figma
  // 52:294, 52:298, 52:310, 54:408 — popovers all sit at the top of the
  // page with arrows pointing up at the nav). Step 4 anchors on the
  // 3-dots action menu inside the demo offer row (Figma 54:388).
  'step-1-listings': {
    element: '[data-tour-step="agent-my-properties-tab"]',
    popover: {
      title: 'Step 1 of 5',
      description:
        'This is your <strong>properties page.</strong> We’ve added an example property so you can see how it looks when you’re active.',
      side: 'bottom',
      align: 'start',
    },
  },
  'step-2-offers-intro': {
    element: '[data-tour-step="agent-offers-tab"]',
    popover: {
      title: 'Step 2 of 5',
      description:
        'This is your <strong>offers page</strong> where investor offers appear. We’ve shown you an example so you know what to expect.<br /><br />Real offers include price, closing timeline, earnest money, and investor notes — everything you need to review with your client.',
      side: 'bottom',
      align: 'start',
    },
  },
  'step-3-offers-email': {
    element: '[data-tour-step="agent-offers-tab"]',
    popover: {
      title: 'Step 3 of 5',
      description:
        'Once you receive an offer from an investor you’ll receive an email.<br /><br />The offer is negotiable. You should contact that investor to discuss the offer.',
      side: 'bottom',
      align: 'start',
    },
  },
  'step-4-offers-actions': {
    element: '[data-tour-step="agent-offer-actions"]',
    popover: {
      // Anchor to the RIGHT of the 3-dots trigger so the auto-opened
      // action menu (which expands to the left/below the trigger) stays
      // fully visible while the popover hangs out to the side.
      title: 'Step 4 of 5',
      description:
        'Be sure to respond to each offer. You can engage, accept, or reject an offer by clicking the three dots here.',
      side: 'right',
      align: 'start',
    },
  },
  'step-5-next-submit': {
    // Step 5 anchors on the (locked) "Submit a property" navbar button —
    // the popover copy teases the next step (the submit-property page),
    // so the spotlight should land on the same button per Figma 54:408.
    element: '[data-tour-step="agent-listings-submit-button"]',
    popover: {
      title: 'Step 5 of 5',
      description:
        'Next we’ll look at how to submit a property. Once you complete your training this will be your first step.',
      side: 'bottom',
      align: 'end',
    },
  },
  'submit-intro': {
    element: '[data-tour-step="agent-submit-address"]',
    popover: {
      description:
        'Here is your <strong>submit a property page.</strong> You’ll come here to enter details about a new property you’d like to submit for offers.<br /><br />Take some time to fill out the details of one of your properties now.',
      side: 'right',
      align: 'start',
    },
  },
  'submit-actions': {
    element: '[data-tour-step="agent-submit-button"]',
    popover: {
      description:
        'Once you’ve finished entering the property details, you can select <strong>“Submit to Investors”</strong> to submit the property to our investor database.<br /><br />If you aren’t ready to submit yet, you can select <strong>“Save Draft”</strong> to save your progress and submit the property later.',
      side: 'top',
      align: 'end',
    },
  },
}

/**
 * Mobile breakpoint matches navbar's `tabletDown` (largeTablet - 1 = 1023px).
 * Below this width the desktop nav tabs are hidden and the hamburger drawer
 * takes over, so the tour anchors that target navbar tabs (steps 1-3) need
 * to swing onto the hamburger button instead.
 */
const MOBILE_MEDIA_QUERY = '(max-width: 1023px)'

function isMobileViewport(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches
}

/**
 * Mobile re-anchor mapping: each numbered tour stop whose desktop anchor
 * lives inside the (display:none) navbar tab list points at its
 * `*-drawer` counterpart on mobile. Navbar opens the drawer for these
 * phases (see Navbar.tsx training-drawer effect) so the popover lands
 * on a visible link.
 */
const MOBILE_DRAWER_SELECTORS: Partial<Record<AgentTrainingPhaseId, string>> = {
  'step-1-listings': '[data-tour-step="agent-my-properties-tab-drawer"]',
  'step-2-offers-intro': '[data-tour-step="agent-offers-tab-drawer"]',
  'step-3-offers-email': '[data-tour-step="agent-offers-tab-drawer"]',
}

/**
 * Resolve the driver.js step for a phase against the current viewport.
 *
 * On desktop (>=1024px) this is mostly a passthrough. On mobile we make
 * two adjustments:
 *
 * 1. **Steps 1-3** target navbar tabs that live inside the mobile
 *    drawer. Navbar's training-drawer effect forces the drawer open
 *    during these phases; we re-anchor the popover onto the drawer
 *    copy of the link (a separate `*-drawer` data-tour-step value) so
 *    driver.js doesn't try to anchor on the desktop nav links (which
 *    are still in the DOM but hidden via display:none and so collapse
 *    to a 0,0 bounding rect).
 * 2. **Step 4** anchors on the offer-row 3-dots button. The button sits
 *    at the right edge of the card and its dropdown menu opens
 *    leftward, so a `side: 'right'` popover (the desktop default)
 *    overlaps the open menu on narrow viewports. Flip the popover to
 *    `side: 'top'` so the menu stays fully visible below it.
 *
 * Step 5 (Submit-a-property navbar button) and the submit-* phases are
 * visible at every breakpoint and pass through unchanged.
 */
export function resolvePhaseStep(phase: AgentTrainingPhaseId): DriveStep {
  const base = PHASE_STEPS[phase]
  if (!isMobileViewport()) return base
  const drawerSelector = MOBILE_DRAWER_SELECTORS[phase]
  if (drawerSelector) {
    return {
      ...base,
      element: drawerSelector,
      popover: {
        ...base.popover,
        side: 'bottom',
        align: 'start',
      },
    }
  }
  if (phase === 'step-4-offers-actions') {
    // Mobile spotlight anchors on the OPEN dropdown menu (not the 3-dot
    // trigger) so the cutout includes the Engage / Accept / Reject
    // items the bottom sheet is talking about. The bottom sheet's
    // position is anchor-driven (always bottom), so `side`/`align`
    // here only affect the cutout geometry — keeping `side: 'top'`
    // means the popover sits ABOVE the menu rect, which on mobile
    // gets overridden by the bottom-sheet CSS anyway.
    return {
      ...base,
      element: '[data-tour-step="agent-offer-actions-menu"]',
      popover: {
        ...base.popover,
        side: 'top',
        align: 'end',
      },
    }
  }
  return base
}

export function getNextPhase(current: AgentTrainingPhaseId): AgentTrainingPhaseId | null {
  const idx = PHASE_ORDER.indexOf(current)
  if (idx === -1 || idx === PHASE_ORDER.length - 1) return null
  return PHASE_ORDER[idx + 1]
}

export function getPreviousPhase(current: AgentTrainingPhaseId): AgentTrainingPhaseId | null {
  const idx = PHASE_ORDER.indexOf(current)
  if (idx <= 0) return null
  return PHASE_ORDER[idx - 1]
}

/** True when the phase is one of the five numbered tour stops. */
export function isNumberedStep(phase: AgentTrainingPhaseId): boolean {
  return phase.startsWith('step-')
}
